import React, { useState, useEffect } from "react";
import { isEmpty } from "../libs/isEmpty";
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from "@material-ui/core/CardHeader";
import Typography from '@material-ui/core/Typography';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { Grid } from "@material-ui/core";
import TextFieldFullWidth from "../components/TextFieldFullWidth";
import { useFormFields } from "../libs/hooksLib";
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MapApp from './MapApp';
import { interesseomraadeLayer, interesseomraadeId } from '../libs/mapStyles';
import { useCardStyles } from '../styles/commonStyles';
import ModalConfirm from './ModalConfirm';

const useStyles = makeStyles(theme => ({
  mapContainer: {
    height: 400,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

export default function InteresseOmraadeView({
  ...props
}) {

  const initialState = {
       interesseomraade: "",
       paavisning: false,
       active: true,
       kontaktperson: "",
       kontaktadresse: "",
       forsyningsart: "",
       forsyningsart_andet: "",
       bemaerkning_omr: "",
       ler20signed: false,
       interesse_ft: "",
       sync_enable: false,
       sync_ts: "",
       sync_message: "",
       geom: null
    }

  const cardClasses = useCardStyles();
  const classes = useStyles();
  const [isVendorless, setIsVendorless] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [fields, setValues, handleFieldChange] = useFormFields(initialState);
  const [formCreateMode, setFormCreateMode] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    if (isEmpty(props.vendorMeta)) {
      setIsVendorless(true)
    } else {
      setIsVendorless(false)
    }
  }, [props.vendorMeta])

  useEffect(() => {
    getValues()
  }, [props.omraade])

  async function handleSubmit(event) {
    event.preventDefault();
    setIsLoading(true);

    let body = { body: fields }

    if (formCreateMode) {
      props.create(body)
    } else {
      props.edit(body)
    }
  }

  function handleFeaturetypeButtonClick() {
    if (!fields.interesse_ft || !props.omraade.interesseomraade) {
      return;
    }
    setConfirmDialogOpen(true);
  }

  async function handleConfirmSync(confirmed) {
    setConfirmDialogOpen(false);
    
    if (!confirmed) {
      return;
    }

    setIsSyncing(true);
    
    // Wait for update and delay to complete before reloading
    await Promise.all([
      new Promise(resolve => setTimeout(resolve, 10000)),
      props.syncInteresseomr(props.omraade.interesseomraade)
    ])
    .then(() => {
      console.log('Sync completed')
      getValues();
    });
    
    setIsSyncing(false);
  }

  function getValues() {
    if (isEmpty(props.omraade)) {
      setValues(initialState)
      setFormCreateMode(true)
    } else {
      // Ensure all fields have defined values
      // Preserve current geom to avoid overwriting during sync
      const safeValues = {
        ...initialState,
        ...props.omraade,
        interesseomraade: props.omraade.interesseomraade ?? '',
        kontaktperson: props.omraade.kontaktperson ?? '',
        kontaktadresse: props.omraade.kontaktadresse ?? '',
        forsyningsart: props.omraade.forsyningsart ?? '',
        forsyningsart_andet: props.omraade.forsyningsart_andet ?? '',
        bemaerkning_omr: props.omraade.bemaerkning_omr ?? '',
        interesse_ft: props.omraade.interesse_ft ?? '',
        sync_enable: props.omraade.sync_enable ?? false,
        sync_ts: props.omraade.sync_ts ?? '',
        sync_message: props.omraade.sync_message ?? '',
        paavisning: props.omraade.paavisning ?? false,
        active: props.omraade.active ?? true,
        ler20signed: props.omraade.ler20signed ?? false,
        geom: fields.geom // Preserve current geom, don't sync
      };
      setValues(safeValues)
      setFormCreateMode(false)
    }
  }

  // Handle validation
  function validateForm() {
    let errors = []

    if (fields.interesseomraade.length === 0) {
      errors.push("Interesseområde: må ikke være tom.")
    }

    if (errors.length > 0) {
      return false
    } else {
      return true
    }
  }

  return (
    <>
    <Card
      className={cardClasses.root}
      raised
    >
      <CardHeader title="Interesseområde detaljer" />
      <CardContent>
        {isEmpty(props.omraade) ? (
          <>
          <ListItem className={classes.listitem} key={"noInteresseOmraade"}>
        <ListItemText>
         Intet interesseområde valgt.
        </ ListItemText>
          </ListItem>        
          </>
        ) : (
          <>
          <form noValidate autoComplete="off"
        onSubmit={handleSubmit}
        key={'interesseOmraadeForm'}>
        <Grid 
        container
        direction="column"
        justifyContent="flex-start"
        spacing={2}>
          <Grid item>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">
                  Interesseområde-nummer
                </Typography>
                <Typography variant="body2">
                  {fields.interesseomraade || '-'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">
                  LER 2.0 Signed
                </Typography>
                <Typography variant="body2">
                  {fields.ler20signed ? 'Ja' : 'Nej'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">
                  Kontaktperson
                </Typography>
                <Typography variant="body2">
                  {fields.kontaktperson || '-'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">
                  Kontaktadresse
                </Typography>
                <Typography variant="body2">
                  {fields.kontaktadresse || '-'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">
                  Forsyningsart
                </Typography>
                <Typography variant="body2">
                  {fields.forsyningsart || '-'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">
                  Forsyningsart andet
                </Typography>
                <Typography variant="body2">
                  {fields.forsyningsart_andet || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Bemærkning
                </Typography>
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {fields.bemaerkning_omr || '-'}
                </Typography>
              </Grid>
            </Grid>
            <FormHelperText>For at ændre i informationerne herover, skal ændringerne foretages på LER og området skal uploades igen. Ellers kontakt support.</FormHelperText>
          </Grid>
          <Grid item>
            <Typography variant="h6" style={{ marginBottom: 8 }}>
              System indstillinger
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="active"
                      checked={fields.active}
                      onChange={handleFieldChange}
                      color="primary"
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Aktiv</Typography>}
                />
                <FormHelperText>Angiver om interesseområdet er aktivt og om der skal hentes forespørgsler for dette område</FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="paavisning"
                      checked={fields.paavisning}
                      onChange={handleFieldChange}
                      color="primary"
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Området kræver påvisning</Typography>}
                />
                <FormHelperText>Angiver om der skal svares at "Påvisning er påkrævet" i ledningsbesvarelsen. Denne indstilling er gældende for hele interesseområdet. Hvis denne er aktiv, vil der ikke blive leveret ledningsoplysninger.</FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="sync_enable"
                      checked={fields.sync_enable}
                      onChange={handleFieldChange}
                      color="primary"
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Synkroniser geometri automatisk</Typography>}
                />
                <FormHelperText>Angiver om interesseområdet skal synkroniseres automatisk baseret på den valgte featuretype. Hvis denne er aktiv, vil interesseområdet blive opdateret dagligt</FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink id="featuretype-select-label">
                    Featuretype
                  </InputLabel>
                  <Select
                    labelId="featuretype-select-label"
                    id="interesse_ft"
                    name="interesse_ft"
                    value={fields.interesse_ft}
                    onChange={handleFieldChange}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Vælg featuretype</em>
                    </MenuItem>
                    {props.featuretypes && props.featuretypes
                      .filter(ft => ft.is_interesse === true)
                      .map(ft => (
                        <MenuItem key={ft.ft_id} value={ft.ft_id}>
                          {ft.alias}
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>Vælg hvilken featuretype der skal bruges til interesseområde. Hvis denne er sat, vil den første feature blive brugt som interesseområde og overskrive eksisterende uden varsel.</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>      
        </Grid>
        <Grid item container spacing={2}>
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              style={{ marginTop: 16}}
              type="submit"
              disabled={!validateForm()}
            >
              {formCreateMode ? "Tilføj interesseområde" : "Gem attributter"}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleFeaturetypeButtonClick}
              disabled={!fields.interesse_ft || isSyncing}
              style={{ marginTop: 16}}
              startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSyncing ? 'Synkroniserer...' : 'Synkroniser område nu'}
            </Button>
          </Grid>                   
        </Grid>
        </form>

        {fields.interesse_ft && (
          <Grid item style={{ marginTop: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
            <Typography variant="body2">
              <strong>Seneste synkronisering: </strong>
              {fields.sync_ts ? new Date(fields.sync_ts).toLocaleString('da-DK') : 'Aldrig'}
            </Typography>
            {fields.sync_message && (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                {fields.sync_message}
              </Typography>
            )}
          </Grid>
        )}

        <Grid item className={classes.mapContainer}>
          <Typography variant="h6" gutterBottom>
            Interesseområde geometri
          </Typography>
          {props.omraade.geom && props.omraade.geom.features && props.omraade.geom.features.length > 0 ? (
            <MapApp
              layer={[{
                id: interesseomraadeId,
                geojson: props.omraade.geom,
                style: [interesseomraadeLayer],
              }]}
              search={false}
              height="400px"
              clickable={false}
            />
          ) : (
            <MapApp
              height="400px"
              clickable={false}
            />
          )}
        </Grid>
          </>
        )}
      </CardContent>
    </Card>
    <ModalConfirm
      open={confirmDialogOpen}
      onClose={handleConfirmSync}
      value={false}
      question={[
        'Vil du synkronisere interesseområdet med LER?',
        'Dette vil opdatere interesseområdets geometri baseret på den valgte featuretype og vil overskrive eksisterende interesseområde på LER uden varsel.'
      ]}
    />
    </>    
  )
}